import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { FaFilePdf, FaDownload } from 'react-icons/fa';

const MES_LABELS = {
  janeiro: 'Janeiro', fevereiro: 'Fevereiro', marco: 'Março',
  abril: 'Abril', maio: 'Maio', junho: 'Junho',
  julho: 'Julho', agosto: 'Agosto', setembro: 'Setembro',
  outubro: 'Outubro', novembro: 'Novembro', dezembro: 'Dezembro'
};

const TRIMESTRE_MESES = {
  '1º': ['janeiro', 'fevereiro', 'marco'],
  '2º': ['abril', 'maio', 'junho'],
  '3º': ['julho', 'agosto', 'setembro'],
  '4º': ['outubro', 'novembro', 'dezembro']
};

const TRIMESTRE_ORDER = { '1º': 1, '2º': 2, '3º': 3, '4º': 4 };

const fmtBr = (val) =>
   (val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseBrMoney = (val) => {
  if (!val) return 0;
  const str = String(val).trim();
  if (!str) return 0;
  if (/[a-zA-Z]/.test(str)) return 0;
  if (!/^[\d.,]+$/.test(str)) return 0;
  const clean = str.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  const num = parseFloat(clean);
  return isNaN(num) ? 0 : num;
};

const PieChart = ({ data, total, title, size = 200 }) => {
  if (!data || total === 0) return null;
  const radius = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b', '#8e44ad', '#27ae60', '#d35400'];
  let startAngle = 0;
  const slices = data.map((item, i) => {
    if (!item.value || item.value === 0) return null;
    const pct = item.value / total;
    const angle = pct * 360;
    const endAngle = startAngle + angle;
    const midAngle = startAngle + angle / 2;
    const labelRadius = radius * 0.75;
    const labelX = cx + labelRadius * Math.cos((midAngle * Math.PI) / 180);
    const labelY = cy + labelRadius * Math.sin((midAngle * Math.PI) / 180);
    const x1 = cx + radius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = cy + radius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = cx + radius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = cy + radius * Math.sin((endAngle * Math.PI) / 180);
    const largeArc = angle > 180 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    startAngle = endAngle;
    const showLabel = pct >= 0.05;
    return { path: <path key={i} d={d} fill={colors[i % colors.length]} title={`${item.name}: ${fmtBr(item.value)}`} />, pct, midAngle, labelX, labelY, showLabel, color: colors[i % colors.length] };
  }).filter(Boolean);

  const paths = slices.map(s => s.path);
  const labels = slices.filter(s => s.showLabel).map((s, i) => (
    <text
      key={`label-${i}`}
      x={s.labelX}
      y={s.labelY}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="10"
      fontWeight="700"
      fill="white"
      fontFamily="Montserrat"
    >
      {(s.pct * 100).toFixed(0)}%
    </text>
  ));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <svg width={size} height={size} style={{ overflow: 'visible' }}>
        {paths}
        {labels}
        <circle cx={cx} cy={cy} r={radius * 0.55} fill="white" />
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="11" fontWeight="700" fill="#333" fontFamily="Montserrat">{title}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="#666" fontFamily="Montserrat">{fmtBr(total)}</text>
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px', maxWidth: size + 20 }}>
        {data.map((item, i) => (
          item.value > 0 && (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontFamily: 'Montserrat' }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: colors[i % colors.length] }} />
              <span style={{ color: '#555' }}>{item.name}</span>
              <span style={{ color: '#888' }}>{(item.value / total * 100).toFixed(1)}%</span>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

const Financeiro = () => {
  const [savedReports, setSavedReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [ano, setAno] = useState(new Date().getFullYear()-1);
  const [trimestre, setTrimestre] = useState('1º');
  const [loading, setLoading] = useState(false);

  const anos = [new Date().getFullYear(), new Date().getFullYear() - 1];
  const trimestres = ['1º', '2º', '3º', '4º'];
  const meses = TRIMESTRE_MESES[trimestre] || [];

  useEffect(() => {
    loadSavedReports();
  }, []);

  useEffect(() => {
    loadReport();
  }, [ano, trimestre]);

  const loadSavedReports = async () => {
    try {
      const res = await api.get('/financial-report');
      if (res.data.success) {
        const sorted = res.data.data.sort((a, b) => {
          if (a.ano !== b.ano) return b.ano - a.ano;
          return (TRIMESTRE_ORDER[a.trimestre] || 0) - (TRIMESTRE_ORDER[b.trimestre] || 0);
        });
        setSavedReports(sorted);
      }
    } catch (err) {
      console.error('Erro ao carregar relatórios salvos:', err);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/financial-report/${ano}/${trimestre}`);
      if (res.data.success && res.data.data) {
        setSelectedReport(res.data.data);
      } else {
        setSelectedReport(null);
      }
    } catch {
      setSelectedReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReport = (report) => {
    setAno(report.ano);
    setTrimestre(report.trimestre);
    setSelectedReport(report);
  };

  const calcMesTotais = (items, mesesArr) => {
    return mesesArr.map(m => items.reduce((s, item) => s + parseBrMoney(item[m]), 0));
  };

  const totaisDespesas = selectedReport ? calcMesTotais(selectedReport.despesas, meses) : [];
  const totaisReceitas = selectedReport ? calcMesTotais(selectedReport.receitas, meses) : [];
  const totalDespesasT1 = totaisDespesas.reduce((a, b) => a + b, 0);
  const totalReceitasT1 = totaisReceitas.reduce((a, b) => a + b, 0);
  const saldo = totalReceitasT1 - totalDespesasT1;

  const chartDespesasData = selectedReport
    ? selectedReport.despesas.map(d => ({ name: d.nome, value: parseBrMoney(d[meses[0]]) + parseBrMoney(d[meses[1]]) + parseBrMoney(d[meses[2]]) }))
    : [];
  const chartReceitasData = selectedReport
    ? selectedReport.receitas.map(r => ({ name: r.nome, value: parseBrMoney(r[meses[0]]) + parseBrMoney(r[meses[1]]) + parseBrMoney(r[meses[2]]) }))
    : [];

  const containerStyle = { padding: '20px', maxWidth: '1100px', margin: '0 auto' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' };
  const selectRowStyle = { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' };
  const labelStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '600', color: '#333' };
  const selectStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'Montserrat' };
  const sectionStyle = { marginBottom: '24px' };
  const sectionTitleStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: '15px', fontWeight: '700', color: '#1A3B7D', marginBottom: '10px', borderBottom: '2px solid #1A3B7D', paddingBottom: '4px' };
  const tableWrapperStyle = { overflowX: 'auto' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat, sans-serif', fontSize: '12px' };
  const thStyle = { padding: '8px 6px', backgroundColor: '#f1f5f9', textAlign: 'right', borderBottom: '2px solid #1A3B7D', fontWeight: '700', color: '#1A3B7D', fontSize: '11px' };
  const tdLabelStyle = { textAlign: 'left', padding: '6px 8px' };
  const tdStyle = { textAlign: 'right', padding: '6px' };
  const tdTotalStyle = { textAlign: 'right', padding: '6px', fontWeight: '700' };
  const trStyle = { borderBottom: '1px solid #f0f0f0' };
  const chartsSectionStyle = { marginBottom: '24px' };
  const chartsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', alignItems: 'start' };
  const chartCardStyle = { backgroundColor: '#fff', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #edf2f7', display: 'flex', justifyContent: 'center' };
  const resumoStyle = { display: 'flex', justifyContent: 'center', marginTop: '20px' };
  const resumoCardStyle = { backgroundColor: '#f8f9fa', borderRadius: '16px', padding: '20px 30px', border: '2px solid #1A3B7D', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '300px' };
  const resumoItemStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' };
  const resumoLabelStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: '14px', color: '#333', fontWeight: '600' };
  const resumoValueStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: '16px', fontWeight: '700', color: '#333' };
  const savedReportsStyle = { marginBottom: '20px' };
  const savedReportsHeaderStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '700', color: '#1A3B7D', marginBottom: '10px' };
  const savedReportsGridStyle = { display: 'flex', flexWrap: 'wrap', gap: '10px' };
  const savedReportCardStyle = (active) => ({
    backgroundColor: active ? '#e8f4fd' : '#fff',
    border: active ? '2px solid #1A3B7D' : '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '200px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s'
  });
  const savedReportInfoStyle = { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 };
  const loadingStyle = { textAlign: 'center', padding: '20px', color: '#666', fontFamily: 'Montserrat' };
  const emptyStyle = { textAlign: 'center', padding: '40px 20px', color: '#888', fontFamily: 'Montserrat', fontSize: '14px' };
  const btnPdfStyle = { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: 'Montserrat', textDecoration: 'none' };

  return (
    <Layout title="Financeiro">
      <div style={containerStyle}>
        <div style={headerStyle}>
          <div style={selectRowStyle}>
            <label style={labelStyle}>Ano:</label>
            <select style={selectStyle} value={ano} onChange={e => setAno(parseInt(e.target.value))}>
              {anos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <label style={labelStyle}>Trimestre:</label>
            <select style={selectStyle} value={trimestre} onChange={e => setTrimestre(e.target.value)}>
              {trimestres.map(t => <option key={t} value={t}>{t} Trimestre</option>)}
            </select>
          </div>
        </div>

        {savedReports.filter(r => r.ano === ano).length > 0 && (
          <div style={savedReportsStyle}>
            <div style={savedReportsHeaderStyle}>Relatórios Disponíveis - {ano}</div>
            <div style={savedReportsGridStyle}>
              {savedReports.filter(r => r.ano === ano).map(r => (
                <div
                  key={r._id}
                  style={savedReportCardStyle(
                    selectedReport && selectedReport._id === r._id
                  )}
                  onClick={() => handleSelectReport(r)}
                >
                  <div style={savedReportInfoStyle}>
                    <strong style={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#1A3B7D' }}>
                      {r.trimestre} Trimestre {r.ano}
                    </strong>
                    <span style={{ fontSize: '11px', color: '#666', fontFamily: 'Montserrat' }}>
                      Saldo:{' '}
                      <span style={{ color: r.saldo >= 0 ? '#27ae60' : '#c0392b', fontWeight: '700' }}>
                        R$ {fmtBr(r.saldo)}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <div style={loadingStyle}>Carregando...</div>}

        {!loading && !selectedReport && (
          <div style={emptyStyle}>
            Nenhum relatório disponível para o {trimestre} trimestre de {ano}.
          </div>
        )}

        {selectedReport && (
          <>
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                DESPESAS {selectedReport.trimestre} TRIMESTRE {selectedReport.ano} - R$
              </h3>
              <div style={tableWrapperStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: 'left' }}>Tipo de Conta</th>
                      {meses.map(m => <th key={m} style={thStyle}>{MES_LABELS[m]}</th>)}
                      <th style={thStyle}>Total</th>
                      <th style={thStyle}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReport.despesas.map((d) => {
                      const vals = meses.map(m => parseBrMoney(d[m]));
                      const total = vals.reduce((a, b) => a + b, 0);
                      const pct = totalDespesasT1 > 0 ? ((total / totalDespesasT1) * 100).toFixed(1) + '%' : '0%';
                      return (
                        <tr key={d.nome} style={trStyle}>
                          <td style={tdLabelStyle}>{d.nome}</td>
                          {vals.map((v, i) => <td key={i} style={tdStyle}>{fmtBr(v)}</td>)}
                          <td style={tdTotalStyle}>{fmtBr(total)}</td>
                          <td style={{ ...tdStyle, color: '#666', fontSize: '11px' }}>{pct}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ ...trStyle, fontWeight: 'bold', backgroundColor: '#c0392b', color: 'white' }}>
                      <td style={{ ...tdLabelStyle, color: 'white' }}>TOTAL</td>
                      {totaisDespesas.map((t, i) => <td key={i} style={{ ...tdTotalStyle, color: 'white' }}>{fmtBr(t)}</td>)}
                      <td style={{ ...tdTotalStyle, color: 'white' }}>{fmtBr(totalDespesasT1)}</td>
                      <td style={{ ...tdStyle, color: 'white' }}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                RECEITAS {selectedReport.trimestre} TRIMESTRE {selectedReport.ano} - R$
              </h3>
              <div style={tableWrapperStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: 'left' }}>Tipo de Conta</th>
                      {meses.map(m => <th key={m} style={thStyle}>{MES_LABELS[m]}</th>)}
                      <th style={thStyle}>Total</th>
                      <th style={thStyle}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReport.receitas.map((r) => {
                      const vals = meses.map(m => parseBrMoney(r[m]));
                      const total = vals.reduce((a, b) => a + b, 0);
                      const pct = totalReceitasT1 > 0 ? ((total / totalReceitasT1) * 100).toFixed(1) + '%' : '0%';
                      return (
                        <tr key={r.nome} style={trStyle}>
                          <td style={tdLabelStyle}>{r.nome}</td>
                          {vals.map((v, i) => <td key={i} style={tdStyle}>{fmtBr(v)}</td>)}
                          <td style={tdTotalStyle}>{fmtBr(total)}</td>
                          <td style={{ ...tdStyle, color: '#666', fontSize: '11px' }}>{pct}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ ...trStyle, fontWeight: 'bold', backgroundColor: '#27ae60', color: 'white' }}>
                      <td style={{ ...tdLabelStyle, color: 'white' }}>TOTAL</td>
                      {totaisReceitas.map((t, i) => <td key={i} style={{ ...tdTotalStyle, color: 'white' }}>{fmtBr(t)}</td>)}
                      <td style={{ ...tdTotalStyle, color: 'white' }}>{fmtBr(totalReceitasT1)}</td>
                      <td style={{ ...tdStyle, color: 'white' }}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {(totalDespesasT1 > 0 || totalReceitasT1 > 0) && (
              <div style={chartsSectionStyle}>
                <div style={chartsGridStyle}>
                  <div style={chartCardStyle}>
                    <PieChart data={chartDespesasData} total={totalDespesasT1} title="DESPESAS" size={200} />
                  </div>
                  <div style={chartCardStyle}>
                    <PieChart data={chartReceitasData} total={totalReceitasT1} title="RECEITAS" size={200} />
                  </div>
                </div>
              </div>
            )}

            <div style={resumoStyle}>
              <div style={resumoCardStyle}>
                <div style={resumoItemStyle}>
                  <span style={resumoLabelStyle}>Total Despesas:</span>
                  <span style={resumoValueStyle}>R$ {fmtBr(totalDespesasT1)}</span>
                </div>
                <div style={resumoItemStyle}>
                  <span style={resumoLabelStyle}>Total Receitas:</span>
                  <span style={{ ...resumoValueStyle, color: '#27ae60' }}>R$ {fmtBr(totalReceitasT1)}</span>
                </div>
                <div style={{ ...resumoItemStyle, borderTop: '2px solid #1A3B7D', paddingTop: '12px' }}>
                  <span style={resumoLabelStyle}>SALDO DO TRIMESTRE: </span>
                  <span style={{ ...resumoValueStyle, fontSize: '18px', color: saldo >= 0 ? '#27ae60' : '#c0392b' }}>R$ {fmtBr(saldo)}</span>
                </div>
                {selectedReport.declaracaoUrl && (
                  <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                    <a href={selectedReport.declaracaoUrl} target="_blank" rel="noopener noreferrer" style={btnPdfStyle}>
                      <FaFilePdf size={15} /> <FaDownload size={12} /> Declaração de Aprovação do Conselho
                    </a>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Financeiro;
